import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { ensureHttpsUrl } from 'app/auth/helpers/ssl-auth.util';
import {
  getConnectedEnterpriseNameFromUser,
  getEnterpriseStationCodeFromUser,
  getEnterpriseStationIdFromUser,
} from 'app/services/enterprise-context';
import {
  AccesLogicielCaisseService,
  AccesSyncStatus,
  CreateAccesLogicielCaissePayload,
} from './acces-logiciel-caisse.service';
import { LogicielCaisseModuleService } from './logiciel-caisse-module.service';
import {
  getErpJwtToken,
  isErpAccountPendingEmailVerification,
} from 'app/services/auth/erp-auth-token.util';

const LC_TOKEN_KEY = 'logiciel_caisse_token';
const LC_SESSION_KEY = 'logiciel_caisse_session';
const LC_CODE_PREFIX = 'logiciel_caisse_code_';
const LC_ACCESS_CACHE_PREFIX = 'logiciel_caisse_access_cache_';

/** Vérifications d'existence caisse mises en cache après login ERP (phase 2). */
interface LogicielCaisseAccessCache {
  erpUserId: number;
  erpStationId: number;
  code_station: string;
  resolved_code_station: string;
  bootstrapStatus: LogicielCaisseBootstrapStatus;
  hasErpAcces: boolean;
  sync_ok: boolean;
  back_checked: boolean;
  back_exists: boolean;
  back_has_station: boolean;
  /** ID user back LC — requis pour réutiliser le cache de façon sûre. */
  back_user_id: number | null;
  back_station_id: number | null;
  cachedAt: number;
}

export interface LogicielCaisseBootstrapOptions {
  /** Ignore le cache sync-status et force un appel réseau (credentials frais). */
  forceRefresh?: boolean;
}

export type LogicielCaisseBootstrapStatus = 'ready' | 'needs_password' | 'needs_setup' | 'idle';

export interface LogicielCaisseAuthSession {
  token: string;
  email: string;
  code_station: string;
  userId: number;
  /** ID station back logiciel caisse (table `station` du back). */
  station_id_back: number;
  /** @deprecated Utiliser station_id_back — conservé pour compatibilité. */
  stationId: number;
  name?: string;
  erpUserId?: number;
  /** ID station ERP (table stations gateway) — ne pas utiliser pour les API back caisse. */
  erpStationId?: number;
}

interface BackLoginResponse {
  success?: boolean;
  _token?: string;
  token?: string;
  error?: string;
  user?: {
    id?: number;
    email?: string;
    name?: string;
    stations?: Array<{ id?: number; code_station?: string; label?: string }>;
  };
}

interface BackVerifyResponse {
  success?: boolean;
  user?: {
    id?: number;
    email?: string;
    station_id?: number;
    code_station?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseAuthService {
  private readonly status$ = new BehaviorSubject<LogicielCaisseBootstrapStatus>(
    this.getSession()?.token ? 'ready' : 'idle'
  );

  readonly authStatus$ = this.status$.asObservable();

  private bootstrapInFlight: Observable<LogicielCaisseBootstrapStatus> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly accesService: AccesLogicielCaisseService,
    private readonly moduleService: LogicielCaisseModuleService
  ) {}

  apiBase(): string {
    const raw =
      environment.api_logiciel_caisse ??
      (environment as { base_api_logiciel_caisse?: string }).base_api_logiciel_caisse ??
      '';
    return ensureHttpsUrl(String(raw).replace(/\/$/, ''));
  }

  getLogicielCaisseToken(): string | null {
    const fromKey = localStorage.getItem(LC_TOKEN_KEY);
    if (fromKey?.trim()) {
      return fromKey.trim();
    }
    return this.getSession()?.token ?? null;
  }

  getSession(): LogicielCaisseAuthSession | null {
    try {
      const token = localStorage.getItem(LC_TOKEN_KEY);
      const raw = localStorage.getItem(LC_SESSION_KEY);
      if (!token?.trim() || !raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as LogicielCaisseAuthSession;
      const station_id_back = Number(parsed.station_id_back ?? parsed.stationId);
      if (!Number.isFinite(station_id_back) || station_id_back <= 0 || !parsed?.email || !parsed?.code_station) {
        return null;
      }
      return {
        ...parsed,
        token: token.trim(),
        station_id_back,
        stationId: station_id_back,
      };
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getSession()?.token;
  }

  /** Session caisse déjà enregistrée — évite sync/verify à chaque clic menu. */
  isReadyForNavigation(): boolean {
    const ctx = this.getErpContext();
    if (!ctx) {
      return false;
    }
    this.syncWithErpContext();
    const cached = this.resolveCachedBootstrapStatus(ctx);
    if (cached === 'ready') {
      this.status$.next('ready');
      return true;
    }
    return false;
  }

  /** Statut caisse en cache (phase 2) — sans appel réseau. */
  getCachedAccessStatus(): LogicielCaisseBootstrapStatus | null {
    const ctx = this.getErpContext();
    if (!ctx) {
      return null;
    }
    return this.loadAccessCache(ctx)?.bootstrapStatus ?? null;
  }

  /** Invalide le cache de vérification (ex. après création d'un accès caisse). */
  invalidateAccessCache(): void {
    const erpUserId = this.getErpContext()?.erpUserId;
    if (erpUserId) {
      this.clearAccessCache(erpUserId);
    }
  }

  /** ID utilisateur back caisse (`users.id`) — ne pas confondre avec l’ID ERP. */
  getBackUserId(): number | null {
    const session = this.getSession();
    const id = Number(session?.userId);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  /** ID station back caisse pour les routes `/api/.../station/:id`. */
  getBackStationId(): number | null {
    const session = this.getSession();
    if (!session) {
      return null;
    }
    return session.station_id_back ?? session.stationId ?? null;
  }

  getErpContext(): {
    erpUserId: number;
    erpStationId: number;
    code_station: string;
    email: string;
    name: string;
    enterpriseName: string;
  } | null {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return null;
      }
      const user = JSON.parse(raw);
      const erpUserId = Number(user?.id);
      if (!Number.isFinite(erpUserId) || erpUserId <= 0) {
        return null;
      }
      const enterpriseName = getConnectedEnterpriseNameFromUser(user);
      return {
        erpUserId,
        erpStationId: getEnterpriseStationIdFromUser(user),
        code_station: getEnterpriseStationCodeFromUser(user),
        email: String(user?.email ?? '').trim(),
        name: String(user?.name ?? user?.firstName ?? '').trim(),
        enterpriseName,
      };
    } catch {
      return null;
    }
  }

  /** Code caisse pour la connexion (accès ERP / back), après résolution sync. */
  getEffectiveCodeStation(erpUserId?: number, user?: unknown): string {
    const uid = erpUserId ?? this.getErpContext()?.erpUserId;
    if (uid) {
      const stored = localStorage.getItem(`${LC_CODE_PREFIX}${uid}`);
      if (stored?.trim()) {
        return stored.trim();
      }
    }
    if (user != null) {
      return getEnterpriseStationCodeFromUser(user);
    }
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        return getEnterpriseStationCodeFromUser(JSON.parse(raw));
      }
    } catch {
      /* ignore */
    }
    return getEnterpriseStationCodeFromUser(null);
  }

  /** Réinitialise la session caisse si le compte ou la station ERP a changé. */
  syncWithErpContext(): void {
    const ctx = this.getErpContext();
    if (!ctx) {
      this.clearSession();
      return;
    }

    const session = this.getSession();
    if (!session) {
      return;
    }

    const erpUserId = Number(session.erpUserId);
    const codeStation = String(session.code_station || '').trim().toLowerCase();
    const ctxCode = String(ctx.code_station || '').trim().toLowerCase();
    const sameUser = Number.isFinite(erpUserId) && erpUserId > 0 && erpUserId === ctx.erpUserId;
    const sameStation = codeStation.length > 0 && codeStation === ctxCode;

    if (!sameUser || !sameStation) {
      this.clearSession();
    }
  }

  /**
   * Pipeline complet après login ERP :
   * 1) session LC déjà en cache → prêt
   * 2) vérification existence caisse (cache ou API sync-status)
   * 3) connexion caisse + enregistrement token/session
   */
  bootstrap(options?: LogicielCaisseBootstrapOptions): Observable<LogicielCaisseBootstrapStatus> {
    if (options?.forceRefresh) {
      this.bootstrapInFlight = null;
      const erpUserId = this.getErpContext()?.erpUserId;
      if (erpUserId) {
        this.clearAccessCache(erpUserId);
      }
    }

    if (this.bootstrapInFlight) {
      return this.bootstrapInFlight;
    }

    const ctx = this.getErpContext();
    if (!ctx) {
      return throwError(new Error('Session ERP invalide. Reconnectez-vous.'));
    }

    this.syncWithErpContext();

    // Même avec un statut local « ready », le JWT caisse peut être expiré
    // ou appartenir à une ancienne station. Le pipeline ci-dessous vérifie
    // toujours le token avant d'autoriser la navigation.
    this.bootstrapInFlight = this.verifyAccessExists(ctx, options).pipe(
      switchMap((status) => {
        const hasErpAcces = !!status.erp?.has_acces;
        if (!hasErpAcces) {
          const bootstrap: LogicielCaisseBootstrapStatus = 'needs_setup';
          this.persistAccessCache(ctx, status, bootstrap);
          this.resetCaisseAccessState(ctx);
          this.status$.next(bootstrap);
          return of(bootstrap);
        }

        return this.connectAndCacheSession(ctx, status);
      }),
      tap((status) => {
        this.status$.next(status);
        this.bootstrapInFlight = null;
      }),
      catchError(() => {
        // Sync-status indisponible : tenter un login avec credentials déjà stockés.
        this.bootstrapInFlight = null;
        return this.tryLoginFromStoredCredentials(ctx).pipe(
          tap((status) => this.status$.next(status)),
          catchError(() => {
            this.resetCaisseAccessState(ctx);
            this.status$.next('needs_setup');
            return of<LogicielCaisseBootstrapStatus>('needs_setup');
          })
        );
      })
    );

    return this.bootstrapInFlight;
  }

  /**
   * Phase 2 — vérifie l'existence de l'accès caisse (ERP + back).
   * Cache réutilisé uniquement si JWT présent + IDs LC + statut ready
   * (sinon sync-status pour récupérer les credentials et reconnecter).
   */
  private verifyAccessExists(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    options?: LogicielCaisseBootstrapOptions
  ): Observable<AccesSyncStatus> {
    const cached = options?.forceRefresh ? null : this.loadAccessCache(ctx);
    const hasToken = !!this.getLogicielCaisseToken();
    const cacheUsable =
      !!cached &&
      hasToken &&
      cached.bootstrapStatus === 'ready' &&
      cached.hasErpAcces === true &&
      Number(cached.back_station_id) > 0;

    if (cacheUsable && cached) {
      return of(this.accessCacheToSyncStatus(cached));
    }
    if (cached) {
      this.clearAccessCache(ctx.erpUserId);
    }

    return this.accesService.getSyncStatus(ctx.code_station, ctx.erpStationId).pipe(
      tap((status) => {
        const bootstrap = this.applySyncStatus(status, ctx);
        this.cacheCredentialsFromSyncStatus(ctx, status);
        this.persistAccessCache(ctx, status, bootstrap);
      })
    );
  }

  /**
   * Phase 3 — connexion au logiciel caisse et enregistrement token/session en cache.
   */
  private connectAndCacheSession(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    status: AccesSyncStatus
  ): Observable<LogicielCaisseBootstrapStatus> {
    const bootstrap = this.applySyncStatus(status, ctx);
    const syncBackStationId = this.readBackStationId(status);
    const resolvedCode = this.resolvedCodeStation(status, ctx);

    return this.verifyExistingToken().pipe(
      switchMap(({ valid, verify }) => {
        if (syncBackStationId) {
          this.patchBackStationId(syncBackStationId);
        }

        if (valid && verify) {
          this.ensureSessionFromVerify(verify, ctx, syncBackStationId, resolvedCode);
          if (status.erp?.has_acces) {
            this.persistAccessCache(ctx, status, 'ready');
            return of<LogicielCaisseBootstrapStatus>('ready');
          }
        }

        if (valid && !status.erp?.has_acces) {
          this.clearSession();
        } else if (valid && status.back?.checked && status.back?.exists === false) {
          this.clearSession();
        }

        return this.loginWithResolvedOrFreshCredentials(ctx, status, bootstrap);
      })
    );
  }

  /**
   * Login LC : credentials du sync courant, sinon sync-status frais
   * (le cache access n'embarque jamais le mot de passe).
   */
  private loginWithResolvedOrFreshCredentials(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    status: AccesSyncStatus,
    bootstrap: LogicielCaisseBootstrapStatus
  ): Observable<LogicielCaisseBootstrapStatus> {
    const attempt = (s: AccesSyncStatus): Observable<LogicielCaisseBootstrapStatus> | null => {
      const resolvedCode = this.resolvedCodeStation(s, ctx);
      const syncBackStationId = this.readBackStationId(s);
      const creds = this.resolveLoginCredentials(ctx, s, resolvedCode);
      const loginCode = creds?.code_station || resolvedCode;
      if (!(creds?.email && creds?.password && loginCode)) {
        return null;
      }
      return this.loginWithCredentials(
        creds.email,
        creds.password,
        loginCode,
        ctx,
        syncBackStationId
      ).pipe(
        map(() => 'ready' as const),
        tap(() => this.persistAccessCache(ctx, s, 'ready')),
        catchError(() => {
          this.persistAccessCache(ctx, s, bootstrap);
          return of(bootstrap);
        })
      );
    };

    const first = attempt(status);
    if (first) {
      return first;
    }

    // JWT expiré + cache sans credentials → recharger sync-status pour le mot de passe.
    return this.accesService.getSyncStatus(ctx.code_station, ctx.erpStationId).pipe(
      tap((fresh) => this.cacheCredentialsFromSyncStatus(ctx, fresh)),
      switchMap((fresh) => {
        const again = attempt(fresh);
        if (again) {
          return again;
        }
        const b = this.applySyncStatus(fresh, ctx);
        this.persistAccessCache(ctx, fresh, b);
        return of(b);
      }),
      catchError(() => this.tryLoginFromStoredCredentials(ctx, bootstrap))
    );
  }

  /** Dernier recours : credentials localStorage (dernière connexion réussie). */
  private tryLoginFromStoredCredentials(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    fallback: LogicielCaisseBootstrapStatus = 'needs_setup'
  ): Observable<LogicielCaisseBootstrapStatus> {
    const creds = this.findStoredCredentials(ctx.erpUserId, [
      this.getEffectiveCodeStation(ctx.erpUserId),
      ctx.code_station,
    ]);
    if (!(creds?.email && creds?.password && creds.code_station)) {
      this.resetCaisseAccessState(ctx);
      return of(fallback);
    }
    return this.loginWithCredentials(creds.email, creds.password, creds.code_station, ctx).pipe(
      map(() => 'ready' as const),
      catchError(() => {
        this.resetCaisseAccessState(ctx);
        return of(fallback);
      })
    );
  }

  /**
   * Après connexion ERP (ou rechargement app) :
   * lance les phases 2 et 3 en arrière-plan pour préparer la navigation.
   * Toujours appeler bootstrap : un cache « ready » peut masquer un JWT expiré.
   */
  autoConnectAfterErpLogin(): void {
    if (!this.getErpContext()) {
      return;
    }
    if (!getErpJwtToken() || isErpAccountPendingEmailVerification()) {
      return;
    }
    this.bootstrap({ forceRefresh: true })
      .pipe(catchError(() => of<LogicielCaisseBootstrapStatus>('needs_setup')))
      .subscribe();
  }

  /**
   * Auth logiciel caisse juste après le login ERP (page /login hub).
   * 1) sync-status
   * 2) toujours tenter email+MDP ERP sur le back caisse (même si has_acces=false)
   * 3) ready | needs_password | needs_setup
   */
  authenticateAfterErpLogin(
    email: string,
    password: string
  ): Observable<LogicielCaisseBootstrapStatus> {
    const emailNorm = String(email || '').trim().toLowerCase();
    const pwd = String(password || '');
    const log = (...args: unknown[]) => console.log('[Login LC]', ...args);
    const warn = (...args: unknown[]) => console.warn('[Login LC]', ...args);
    const err = (...args: unknown[]) => console.error('[Login LC]', ...args);

    log('Démarrage authenticateAfterErpLogin', {
      email: emailNorm,
      passwordLength: pwd.length,
      api_logiciel_caisse: (environment as { api_logiciel_caisse?: string }).api_logiciel_caisse,
    });

    return this.bootstrap({ forceRefresh: true }).pipe(
      switchMap((status) => {
        const ctx = this.getErpContext();
        const session = this.getSession();
        log('Résultat bootstrap', {
          status,
          isAuthenticated: this.isAuthenticated(),
          erpContext: ctx
            ? {
                erpUserId: ctx.erpUserId,
                erpStationId: ctx.erpStationId,
                code_station: ctx.code_station,
                email: ctx.email,
              }
            : null,
          lcSession: session
            ? {
                email: session.email,
                code_station: session.code_station,
                userId: session.userId,
                station_id_back: session.station_id_back,
                hasToken: !!session.token,
              }
            : null,
          cachedAccess: ctx ? this.loadAccessCache(ctx) : null,
        });

        if (status === 'ready' && this.isAuthenticated()) {
          log('OK — session logiciel caisse prête');
          return of<LogicielCaisseBootstrapStatus>('ready');
        }

        if (!ctx) {
          err('ÉCHEC — getErpContext() null après login ERP');
          return of<LogicielCaisseBootstrapStatus>('needs_setup');
        }

        if (!pwd) {
          warn('Mot de passe ERP vide — impossible de tenter login LC', { status });
          return of<LogicielCaisseBootstrapStatus>(
            status === 'needs_password' ? 'needs_password' : 'needs_setup'
          );
        }

        // Toujours tenter le MDP ERP sur le back caisse (même si sync dit needs_setup).
        log('Tentative login LC avec mot de passe ERP…', { status });
        return this.tryLoginWithErpPassword(ctx, emailNorm, pwd).pipe(
          map((ok) => {
            if (ok) {
              log('OK — login LC réussi avec MDP ERP');
              return 'ready' as const;
            }
            if (status === 'needs_password') {
              warn('Accès ERP présent mais MDP ERP ≠ MDP caisse → needs_password');
              return 'needs_password' as const;
            }
            warn('Pas d’accès ERP sync + login LC échoué → needs_setup', {
              code_station: ctx.code_station,
              erpStationId: ctx.erpStationId,
              erpUserId: ctx.erpUserId,
            });
            return 'needs_setup' as const;
          })
        );
      }),
      catchError((e) => {
        err('Exception authenticateAfterErpLogin', e);
        return of<LogicielCaisseBootstrapStatus>('needs_setup');
      })
    );
  }

  /**
   * Tente login LC avec le MDP ERP (emails ERP + emails des accès sync-status).
   * Ne dépend pas de has_acces : le compte peut exister sur le back caisse.
   */
  private tryLoginWithErpPassword(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    erpEmail: string,
    erpPassword: string
  ): Observable<boolean> {
    const log = (...args: unknown[]) => console.log('[Login LC][tryErpPassword]', ...args);
    const warn = (...args: unknown[]) => console.warn('[Login LC][tryErpPassword]', ...args);

    return this.accesService.getSyncStatus(ctx.code_station, ctx.erpStationId).pipe(
      tap((status) => {
        this.cacheCredentialsFromSyncStatus(ctx, status);
        const bootstrap = this.applySyncStatus(status, ctx);
        this.persistAccessCache(ctx, status, bootstrap);
        log('sync-status', {
          code_station: status.code_station,
          resolved_code_station: status.resolved_code_station,
          sync_ok: status.sync_ok,
          action: status.action,
          has_acces: status.erp?.has_acces,
          acces_count: status.erp?.count,
          acces_emails: (status.erp?.acces || []).map((a) => a.email),
          has_credentials_in_api: !!(status.credentials?.email && status.credentials?.password),
          password_legacy_hash: status.password_legacy_hash,
          back: status.back,
        });
      }),
      switchMap((status) => {
        const code =
          this.resolvedCodeStation(status, ctx) ||
          this.getEffectiveCodeStation(ctx.erpUserId) ||
          ctx.code_station;
        const backStationId = this.readBackStationId(status);
        const emails = new Set<string>();
        if (erpEmail) {
          emails.add(erpEmail);
        }
        if (ctx.email) {
          emails.add(String(ctx.email).trim().toLowerCase());
        }
        for (const row of status.erp?.acces || []) {
          const e = String(row?.email || '').trim().toLowerCase();
          if (e) {
            emails.add(e);
          }
        }
        // Credentials sync-status (mot de passe clair stocké ERP).
        if (status.credentials?.email && status.credentials?.password) {
          log('Credentials sync-status disponibles — login prioritaire');
          return this.loginWithCredentials(
            status.credentials.email,
            status.credentials.password,
            status.credentials.code_station || code,
            ctx,
            backStationId
          ).pipe(
            map(() => true),
            catchError((e) => {
              warn('Login avec credentials sync échoué', e instanceof Error ? e.message : e);
              return this.tryEmailsWithPassword(
                Array.from(emails).filter(Boolean),
                erpPassword,
                code,
                ctx,
                backStationId
              );
            })
          );
        }

        const list = Array.from(emails).filter(Boolean);
        log('Emails à tester + code_station', {
          list,
          code,
          backStationId,
          has_acces: status.erp?.has_acces,
        });
        if (!list.length || !code) {
          warn('Pas d’email ou code_station pour login LC');
          return of(false);
        }
        return this.tryEmailsWithPassword(list, erpPassword, code, ctx, backStationId);
      }),
      catchError((e) => {
        console.error('[Login LC][tryErpPassword] sync-status erreur', e);
        // Dernier recours : login direct sans sync.
        const code = this.getEffectiveCodeStation(ctx.erpUserId) || ctx.code_station;
        if (!erpEmail || !code) {
          return of(false);
        }
        warn('Fallback login LC direct (sans sync-status)', { email: erpEmail, code });
        return this.loginWithCredentials(erpEmail, erpPassword, code, ctx).pipe(
          map(() => true),
          catchError((err2) => {
            warn('Fallback login LC échoué', err2 instanceof Error ? err2.message : err2);
            return of(false);
          })
        );
      })
    );
  }

  private tryEmailsWithPassword(
    emails: string[],
    password: string,
    code: string,
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    backStationId: number | null
  ): Observable<boolean> {
    const log = (...args: unknown[]) => console.log('[Login LC][tryEmails]', ...args);
    const warn = (...args: unknown[]) => console.warn('[Login LC][tryEmails]', ...args);

    const tryAt = (index: number): Observable<boolean> => {
      if (index >= emails.length) {
        warn('Tous les essais login LC ont échoué', { tried: emails, code });
        return of(false);
      }
      log(`Essai ${index + 1}/${emails.length}`, { email: emails[index], code_station: code });
      return this.loginWithCredentials(emails[index], password, code, ctx, backStationId).pipe(
        map(() => {
          log('Login LC OK', { email: emails[index], code_station: code });
          return true;
        }),
        catchError((e) => {
          warn('Login LC refusé', {
            email: emails[index],
            code_station: code,
            error: e instanceof Error ? e.message : e,
          });
          return tryAt(index + 1);
        })
      );
    };
    return tryAt(0);
  }

  /**
   * Garantit un JWT caisse valide (verify + re-login si expiré).
   * À utiliser avant les appels API LC (ne pas se fier seul à isReadyForNavigation).
   * En cas d'échec, 2ᵉ tentative avec forceRefresh (sync-status + credentials).
   */
  ensureAuthenticatedSession(): Observable<boolean> {
    const run = (forceRefresh: boolean) =>
      this.bootstrap(forceRefresh ? { forceRefresh: true } : undefined).pipe(
        map((status) => status === 'ready' && this.isAuthenticated()),
        catchError(() => of(false))
      );

    return run(false).pipe(switchMap((ok) => (ok ? of(true) : run(true))));
  }

  /** Annule un bootstrap caisse en cours (changement de session ERP). */
  resetBootstrapForNewErpSession(): void {
    this.bootstrapInFlight = null;
  }

  loginWithCredentials(
    email: string,
    password: string,
    code_station: string,
    erpCtx?: ReturnType<LogicielCaisseAuthService['getErpContext']>,
    backStationIdFromSync?: number | null
  ): Observable<LogicielCaisseAuthSession> {
    const ctx = erpCtx ?? this.getErpContext();
    const emailNorm = String(email || '').trim().toLowerCase();
    const code = String(code_station || '').trim();
    const url = `${this.apiBase()}/api/auth/login/admin`;

    return this.http
      .post<BackLoginResponse>(url, {
        email: emailNorm,
        password,
        code_station: code,
      })
      .pipe(
        map((res) => {
          const token = String(res?._token || res?.token || '').trim();
          if (!res?.success || !token) {
            throw new Error(res?.error || 'Connexion logiciel caisse échouée.');
          }
          const session = this.buildSessionFromLogin(
            token,
            emailNorm,
            code,
            res,
            ctx,
            backStationIdFromSync
          );
          this.persistSession(session);
          if (ctx?.erpUserId) {
            this.storeCredentials(ctx.erpUserId, code, {
              email: emailNorm,
              password,
              code_station: code,
            });
          }
          return session;
        }),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as { error?: string; message?: string } | string | null;
          const msg =
            typeof body === 'object' && body?.error
              ? body.error
              : typeof body === 'object' && body?.message
                ? body.message
                : typeof body === 'string' && body.trim()
                  ? body.trim()
                  : err.status === 0
                    ? 'Impossible de joindre le serveur logiciel caisse (vérifiez l’API et CORS).'
                    : 'Échec de la connexion logiciel caisse.';
          return throwError(new Error(msg));
        })
      );
  }

  createAccesAndLogin(payload: CreateAccesLogicielCaissePayload): Observable<LogicielCaisseAuthSession> {
    const ctx = this.getErpContext();
    const withUser: CreateAccesLogicielCaissePayload = {
      ...payload,
      id_user: payload.id_user ?? ctx?.erpUserId,
    };
    const login = () =>
      this.loginWithCredentials(withUser.email, withUser.password, withUser.code_station, ctx ?? undefined);

    return this.accesService.create(withUser).pipe(
      switchMap(() => login()),
      tap(() => this.invalidateAccessCache()),
      switchMap((session) => {
        if (!ctx) {
          return of(session);
        }
        return this.verifyAccessExists(ctx).pipe(
          tap((status) => this.persistAccessCache(ctx, status, 'ready')),
          map(() => session)
        );
      }),
      catchError((err: Error & { status?: number }) => {
        if (err?.status === 409) {
          return login();
        }
        return throwError(err);
      })
    );
  }

  clearSession(): void {
    localStorage.removeItem(LC_TOKEN_KEY);
    localStorage.removeItem(LC_SESSION_KEY);
    this.status$.next('idle');
    this.bootstrapInFlight = null;
  }

  loadStoredCredentials(
    erpUserId: number,
    code_station: string
  ): { email: string; password: string; code_station: string } | null {
    try {
      const raw = localStorage.getItem(this.credsKey(erpUserId, code_station));
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed?.email || !parsed?.password) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Réutilise la session enregistrée au login — évite un second appel sync/verify
   * à chaque navigation vers tickets ou paramètres caisse.
   */
  private resolveCachedBootstrapStatus(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>
  ): LogicielCaisseBootstrapStatus | null {
    const session = this.getSession();
    if (!session?.token) {
      return null;
    }

    const effectiveCode = this.getEffectiveCodeStation(ctx.erpUserId);

    const sessionErpUserId = Number(session.erpUserId);
    if (Number.isFinite(sessionErpUserId) && sessionErpUserId > 0 && sessionErpUserId !== ctx.erpUserId) {
      return null;
    }

    const sessionCode = String(session.code_station || '').trim().toLowerCase();
    const ctxCode = String(ctx.code_station || '').trim().toLowerCase();
    const effective = String(effectiveCode || '').trim().toLowerCase();
    const codeMatches =
      !sessionCode || sessionCode === ctxCode || sessionCode === effective;
    if (!codeMatches) {
      return null;
    }

    const backStationId = Number(session.station_id_back ?? session.stationId);
    if (!Number.isFinite(backStationId) || backStationId <= 0) {
      return null;
    }

    this.moduleService.enableCodes(ctx.code_station, effectiveCode, session.code_station);

    return 'ready';
  }

  private accessCacheKey(erpUserId: number): string {
    return `${LC_ACCESS_CACHE_PREFIX}${erpUserId}`;
  }

  private loadAccessCache(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>
  ): LogicielCaisseAccessCache | null {
    try {
      const raw = localStorage.getItem(this.accessCacheKey(ctx.erpUserId));
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as LogicielCaisseAccessCache;
      if (parsed.erpUserId !== ctx.erpUserId) {
        return null;
      }
      if (parsed.erpStationId !== ctx.erpStationId) {
        return null;
      }
      const cachedCode = String(parsed.code_station || '').trim().toLowerCase();
      const ctxCode = String(ctx.code_station || '').trim().toLowerCase();
      if (cachedCode && ctxCode && cachedCode !== ctxCode) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private persistAccessCache(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    status: AccesSyncStatus,
    bootstrapStatus: LogicielCaisseBootstrapStatus
  ): void {
    const resolved = this.resolvedCodeStation(status, ctx);
    const cache: LogicielCaisseAccessCache = {
      erpUserId: ctx.erpUserId,
      erpStationId: ctx.erpStationId,
      code_station: ctx.code_station,
      resolved_code_station: resolved,
      bootstrapStatus,
      hasErpAcces: !!status.erp?.has_acces,
      sync_ok: !!status.sync_ok,
      back_checked: !!status.back?.checked,
      back_exists: !!status.back?.exists,
      back_has_station: !!status.back?.has_station,
      back_user_id: this.readBackUserId(status),
      back_station_id: this.readBackStationId(status),
      cachedAt: Date.now(),
    };
    if (bootstrapStatus === 'ready') {
      const session = this.getSession();
      if ((!cache.back_user_id || cache.back_user_id <= 0) && session?.userId) {
        cache.back_user_id = Number(session.userId) || null;
      }
      if ((!cache.back_station_id || cache.back_station_id <= 0) && session) {
        const sid = Number(session.station_id_back ?? session.stationId);
        cache.back_station_id = Number.isFinite(sid) && sid > 0 ? sid : cache.back_station_id;
      }
    }
    localStorage.setItem(this.accessCacheKey(ctx.erpUserId), JSON.stringify(cache));
  }

  private clearAccessCache(erpUserId: number): void {
    localStorage.removeItem(this.accessCacheKey(erpUserId));
  }

  private accessCacheToSyncStatus(cache: LogicielCaisseAccessCache): AccesSyncStatus {
    return {
      code_station: cache.code_station,
      resolved_code_station: cache.resolved_code_station,
      sync_ok: cache.sync_ok,
      erp: {
        has_acces: cache.hasErpAcces,
        count: cache.hasErpAcces ? 1 : 0,
        acces: [],
      },
      back: {
        checked: cache.back_checked,
        exists: cache.back_exists,
        has_station: cache.back_has_station,
        user_id: cache.back_user_id,
        station_id: cache.back_station_id,
      },
      credentials: null,
    };
  }

  private cacheCredentialsFromSyncStatus(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    status: AccesSyncStatus
  ): void {
    const creds = status.credentials;
    if (!creds?.email || !creds?.password || !ctx.erpUserId) {
      return;
    }
    const code = String(
      creds.code_station ||
        status.resolved_code_station ||
        status.code_station ||
        ctx.code_station
    ).trim();
    if (!code) {
      return;
    }
    this.storeCredentials(ctx.erpUserId, code, {
      email: creds.email,
      password: creds.password,
      code_station: code,
    });
  }

  private resolvedCodeStation(
    status: AccesSyncStatus,
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>
  ): string {
    return String(
      status.resolved_code_station ||
        status.code_station ||
        this.getEffectiveCodeStation(ctx.erpUserId) ||
        ctx.code_station
    ).trim();
  }

  private resolveLoginCredentials(
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    status: AccesSyncStatus,
    resolvedCode: string
  ): { email: string; password: string; code_station: string } | null {
    if (status.credentials?.email && status.credentials?.password) {
      return {
        email: status.credentials.email,
        password: status.credentials.password,
        code_station: status.credentials.code_station || resolvedCode,
      };
    }

    const fromStore = this.findStoredCredentials(ctx.erpUserId, [
      resolvedCode,
      this.getEffectiveCodeStation(ctx.erpUserId),
      ctx.code_station,
      String(status.code_station || '').trim(),
    ]);
    if (fromStore) {
      return fromStore;
    }

    return null;
  }

  private applySyncStatus(
    status: AccesSyncStatus,
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>
  ): LogicielCaisseBootstrapStatus {
    const effectiveCode = String(
      status.resolved_code_station || status.code_station || ctx.code_station
    ).trim();
    if (effectiveCode) {
      this.storeResolvedCodeStation(ctx.erpUserId, effectiveCode);
    }
    this.moduleService.enableCodes(ctx.code_station, effectiveCode);

    const hasErpAcces = !!status.erp?.has_acces;
    const backReady = !!status.sync_ok || !!status.back?.has_station;

    if (hasErpAcces && (backReady || !!status.back?.exists)) {
      return 'needs_password';
    }
    return 'needs_setup';
  }

  private looksLikeJwt(token: string): boolean {
    const parts = String(token || '').split('.');
    return parts.length === 3 && parts.every((p) => p.length > 0);
  }

  private verifyExistingToken(): Observable<{ valid: boolean; verify?: BackVerifyResponse }> {
    const token = this.getLogicielCaisseToken();
    if (!token || !this.looksLikeJwt(token)) {
      if (token && !this.looksLikeJwt(token)) {
        this.clearSession();
      }
      return of({ valid: false });
    }
    const url = `${this.apiBase()}/api/auth/verify`;
    return this.http
      .get<BackVerifyResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        map((res) => {
          const jwtStationId = Number(res?.user?.station_id);
          if (Number.isFinite(jwtStationId) && jwtStationId > 0) {
            this.patchBackStationId(jwtStationId);
          }
          return { valid: !!res?.success, verify: res };
        }),
        catchError(() => {
          this.clearSession();
          return of({ valid: false });
        })
      );
  }

  private ensureSessionFromVerify(
    verify: BackVerifyResponse,
    ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>,
    syncBackStationId: number | null,
    resolvedCode: string
  ): void {
    const token = this.getLogicielCaisseToken();
    if (!token) {
      return;
    }

    const parsed = this.readPartialSession();
    const verifiedStationId = Number(verify?.user?.station_id);
    const parsedStationId = Number(parsed?.station_id_back ?? parsed?.stationId);
    const station_id_back =
      Number.isFinite(verifiedStationId) && verifiedStationId > 0
        ? verifiedStationId
        : syncBackStationId != null && Number.isFinite(syncBackStationId) && syncBackStationId > 0
          ? syncBackStationId
          : Number.isFinite(parsedStationId) && parsedStationId > 0
            ? parsedStationId
            : 0;

    if (!Number.isFinite(station_id_back) || station_id_back <= 0) {
      return;
    }

    const email = String(parsed?.email ?? verify?.user?.email ?? ctx.email).trim();
    const code_station = String(
      parsed?.code_station ?? verify?.user?.code_station ?? resolvedCode
    ).trim();
    const userId = Number(parsed?.userId ?? verify?.user?.id) || 0;

    if (!email || !code_station) {
      return;
    }

    this.persistSession({
      token,
      email,
      code_station,
      userId,
      station_id_back,
      stationId: station_id_back,
      name: parsed?.name,
      erpUserId: ctx.erpUserId,
      erpStationId: ctx.erpStationId,
    });
  }

  private buildSessionFromLogin(
    token: string,
    email: string,
    code_station: string,
    res: BackLoginResponse,
    ctx: ReturnType<LogicielCaisseAuthService['getErpContext']>,
    backStationIdFromSync?: number | null
  ): LogicielCaisseAuthSession {
    const stations = Array.isArray(res.user?.stations) ? res.user!.stations! : [];
    const match =
      stations.find(
        (s) => String(s.code_station ?? '').trim().toLowerCase() === code_station.toLowerCase()
      ) ?? stations[0];
    const fromLogin = Number(match?.id);
    const fromSync = Number(backStationIdFromSync);
    const station_id_back =
      Number.isFinite(fromSync) && fromSync > 0
        ? fromSync
        : Number.isFinite(fromLogin) && fromLogin > 0
          ? fromLogin
          : NaN;
    if (!Number.isFinite(station_id_back) || station_id_back <= 0) {
      throw new Error('Station logiciel caisse introuvable après connexion.');
    }
    return {
      token,
      email,
      code_station,
      userId: Number(res.user?.id) || 0,
      station_id_back,
      stationId: station_id_back,
      name: res.user?.name,
      erpUserId: ctx?.erpUserId,
      erpStationId: ctx?.erpStationId,
    };
  }

  private readBackStationId(status: AccesSyncStatus): number | null {
    const id = Number(status.back?.station_id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private readBackUserId(status: AccesSyncStatus): number | null {
    const id = Number(status.back?.user_id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private patchBackStationId(station_id_back: number): void {
    if (!Number.isFinite(station_id_back) || station_id_back <= 0) {
      return;
    }
    const token = this.getLogicielCaisseToken();
    if (!token) {
      return;
    }
    try {
      const raw = localStorage.getItem(LC_SESSION_KEY);
      const parsed = raw ? (JSON.parse(raw) as Partial<LogicielCaisseAuthSession>) : {};
      const next: LogicielCaisseAuthSession = {
        token,
        email: String(parsed.email ?? ''),
        code_station: String(parsed.code_station ?? ''),
        userId: Number(parsed.userId) || 0,
        station_id_back,
        stationId: station_id_back,
        name: parsed.name,
        erpUserId: parsed.erpUserId,
        erpStationId: parsed.erpStationId,
      };
      if (next.email && next.code_station && next.userId > 0) {
        this.persistSession(next);
      } else {
        localStorage.setItem(
          LC_SESSION_KEY,
          JSON.stringify({ ...parsed, token, station_id_back, stationId: station_id_back })
        );
      }
    } catch {
      /* ignore */
    }
  }

  private findStoredCredentials(
    erpUserId: number,
    codes: string[]
  ): { email: string; password: string; code_station: string } | null {
    const seen = new Set<string>();
    for (const code of codes) {
      const normalized = String(code || '').trim();
      if (!normalized || seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      const creds = this.loadStoredCredentials(erpUserId, normalized);
      if (creds?.email && creds?.password) {
        return creds;
      }
    }

    const prefix = `logiciel_caisse_creds_${erpUserId}_`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) {
        continue;
      }
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || '');
        if (parsed?.email && parsed?.password) {
          return parsed;
        }
      } catch {
        /* ignore */
      }
    }
    return null;
  }

  private storeResolvedCodeStation(erpUserId: number, code_station: string): void {
    const code = String(code_station || '').trim();
    if (erpUserId > 0 && code) {
      localStorage.setItem(`${LC_CODE_PREFIX}${erpUserId}`, code);
    }
  }

  private persistSession(session: LogicielCaisseAuthSession): void {
    localStorage.setItem(LC_TOKEN_KEY, session.token);
    localStorage.setItem(LC_SESSION_KEY, JSON.stringify(session));
    const ctx = this.getErpContext();
    const effectiveCode = ctx
      ? this.getEffectiveCodeStation(ctx.erpUserId)
      : String(session.code_station || '').trim();
    this.moduleService.enableCodes(
      ctx?.code_station,
      effectiveCode,
      session.code_station
    );
    this.status$.next('ready');
  }

  private credsKey(erpUserId: number, code_station: string): string {
    return `logiciel_caisse_creds_${erpUserId}_${String(code_station || '').trim()}`;
  }

  private storeCredentials(
    erpUserId: number,
    code_station: string,
    creds: { email: string; password: string; code_station: string }
  ): void {
    if (!erpUserId) {
      return;
    }
    localStorage.setItem(this.credsKey(erpUserId, code_station), JSON.stringify(creds));
  }

  private readPartialSession(): Partial<LogicielCaisseAuthSession> | null {
    try {
      const raw = localStorage.getItem(LC_SESSION_KEY);
      return raw ? (JSON.parse(raw) as Partial<LogicielCaisseAuthSession>) : null;
    } catch {
      return null;
    }
  }

  private clearStoredCredentialsForUser(erpUserId: number): void {
    if (!erpUserId) {
      return;
    }
    const prefix = `logiciel_caisse_creds_${erpUserId}_`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(`${LC_CODE_PREFIX}${erpUserId}`);
  }

  private resetCaisseAccessState(ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>): void {
    this.clearSession();
    this.clearStoredCredentialsForUser(ctx.erpUserId);
    this.moduleService.disable(ctx.code_station);
    this.moduleService.disable(this.getEffectiveCodeStation(ctx.erpUserId));
  }
}
